/**
 * Small presentational chips shared across the Coral Placer — color dots,
 * color/growth pills, and a generic toggle chip used by the filters panel.
 */
import { COLOR_HEX } from './catalog';
import type { ColorTag, GrowthForm } from './types';

export function ColorDots({ colors }: { readonly colors: readonly ColorTag[] }) {
  return (
    <span className="inline-flex items-center gap-1">
      {colors.map((c) => (
        <span
          key={c}
          title={c}
          className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-black/20"
          style={{ backgroundColor: COLOR_HEX[c] }}
        />
      ))}
    </span>
  );
}

export function ColorTagPill({ color }: { readonly color: ColorTag }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-surface-stroke bg-surface-glass px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-2">
      <span
        className="inline-block h-2 w-2 rounded-full"
        style={{ backgroundColor: COLOR_HEX[color] }}
      />
      {color}
    </span>
  );
}

export function GrowthPill({ form }: { readonly form: GrowthForm }) {
  return (
    <span className="inline-flex items-center rounded-full border border-accent-cyan/30 bg-accent-cyan/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-accent-cyan">
      {form}
    </span>
  );
}

export interface ToggleChipProps {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly label: string;
  readonly color?: string;
}

export function ToggleChip({ active, onClick, label, color }: ToggleChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
        active
          ? 'border-accent/60 bg-accent-soft text-text-1'
          : 'border-surface-stroke bg-surface-glass text-text-3 hover:text-text-2'
      }`}
    >
      {color && (
        <span
          className="inline-block h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: color }}
        />
      )}
      {label}
    </button>
  );
}
